import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiError, deliveryApi, readManagementToken, rememberManagementToken,
  type DeliveryChallengeDto, type DeliveryChannel, type DeliveryStateDto, type OrderStatus,
} from '../api';

/**
 * TICK-060 — Screens B and C-LINE of the ticket-delivery journey.
 *
 * Two rules shape everything here:
 *
 * - The section is only reachable with the buyer's management capability. Without it we render
 *   nothing at all rather than an error: a customer who opened their order code on a second device
 *   has not done anything wrong, and a broken-looking panel on the page where they are waiting for
 *   a payment to clear is worse than no panel.
 * - The panel never claims a ticket exists. `ticketReady` comes from the server, which only sets it
 *   when the payment is `paid` *and* the primary channel is verified.
 */

/** The outcome word the LINE callback redirects back with. Never carries a token or a user ID. */
type LineCallbackOutcome = 'connected' | 'friend_required' | 'denied' | 'failed';

const CALLBACK_MESSAGES: Record<LineCallbackOutcome, { tone: 'ok' | 'warn' | 'error'; text: string }> = {
  connected: { tone: 'ok', text: 'เชื่อมต่อ JamTick LINE สำเร็จ — บัตรจะถูกส่งทาง LINE' },
  friend_required: { tone: 'warn', text: 'เชื่อมบัญชี LINE แล้ว แต่ยังไม่ได้เพิ่ม JamTick เป็นเพื่อน — เพิ่มเพื่อนเพื่อรับบัตร' },
  denied: { tone: 'warn', text: 'ยกเลิกการเชื่อมต่อ LINE แล้ว — เลือกช่องทางรับตั๋วได้อีกครั้ง' },
  failed: { tone: 'error', text: 'เชื่อมต่อ LINE ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง' },
};

const CHANNEL_LABELS: Record<DeliveryChannel, string> = {
  line: 'JamTick LINE',
  email: 'อีเมล',
  sms: 'SMS',
};

/** Copy is a capability claim, not a security claim — "check delivery status", never "most secure". */
const CHANNEL_HINTS: Record<DeliveryChannel, string> = {
  line: 'แนะนำ — เปิดบัตรจาก LINE และตรวจสถานะการส่งได้',
  email: 'รับลิงก์บัตรทางอีเมลที่กรอกไว้ตอนชำระเงิน',
  sms: 'รับลิงก์บัตรทาง SMS ที่เบอร์ที่กรอกไว้ตอนชำระเงิน',
};

const UNAVAILABLE_HINTS: Record<NonNullable<DeliveryChannelDtoReason>, string> = {
  NO_RECIPIENT: 'ไม่มีข้อมูลติดต่อสำหรับช่องทางนี้ในคำสั่งซื้อ',
  VERIFICATION_UNAVAILABLE: 'ยังไม่เปิดให้ยืนยันช่องทางนี้',
  CHANNEL_DISABLED: 'ช่องทางนี้ปิดให้บริการชั่วคราว',
};

type DeliveryChannelDtoReason = DeliveryStateDto['options'][number]['unavailableReason'];

/** Waiting on the JamTick OA's `follow` webhook — bounded, because it is a nicety, not the mechanism. */
const FRIENDSHIP_POLL_INTERVAL_MS = 5000;
const FRIENDSHIP_POLL_ATTEMPTS = 24;

export interface DeliveryChannelPanelProps {
  orderCode: string;
  eventSlug?: string;
  orderStatus: OrderStatus;
  /** Lets the page re-fetch the order the moment a ticket becomes available. */
  onTicketReady?: () => void;
  /** TICK-159 — who to contact when no channel can be used. The event's own address, never a platform one. */
  contactEmail?: string;
}

export default function DeliveryChannelPanel({
  orderCode, eventSlug, orderStatus, onTicketReady, contactEmail,
}: DeliveryChannelPanelProps) {
  const [state, setState] = useState<DeliveryStateDto | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  /** Nothing renders until the probe has answered — a flash of the recovery notice on a deploy
   * with the feature off would be exactly the wrong first thing a waiting customer reads. */
  const [probed, setProbed] = useState(false);
  const [busy, setBusy] = useState<DeliveryChannel | 'refresh' | 'clear' | 'resend' | 'otp' | 'recovery-otp' | null>(null);
  const [challenge, setChallenge] = useState<DeliveryChallengeDto | null>(null);
  const [otp, setOtp] = useState('');
  const [recoverySent, setRecoverySent] = useState('');
  /** TICK-167 — which recovery was last requested; 'sms' is what puts the code entry on screen. */
  const [recoveryChannel, setRecoveryChannel] = useState<'email' | 'sms' | null>(null);
  const [recoveryOtp, setRecoveryOtp] = useState('');
  /** Held in state as well as sessionStorage: a private-mode write can fail, and the tab should still work. */
  const [recoveredToken, setRecoveredToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [callbackOutcome, setCallbackOutcome] = useState<LineCallbackOutcome | null>(null);
  const token = recoveredToken ?? readManagementToken(orderCode);
  const readyNotified = useRef(false);

  // The redirect back from LINE is the only thing that ever puts a query string on this page, and
  // it is removed immediately so a copied or shared URL is the plain order link again.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('line');
    if (!outcome) return;
    if (outcome in CALLBACK_MESSAGES) setCallbackOutcome(outcome as LineCallbackOutcome);
    params.delete('line');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, []);

  const load = useCallback(async () => {
    try {
      // The unauthenticated probe first, and always: a 404 here is the rollout flag being off for
      // this deploy, which is the one case where the whole section must not exist. Without it, a
      // build with the feature disabled would show every customer a recovery notice for something
      // that was never deployed.
      const publicState = await deliveryApi.getPublicState(orderCode, eventSlug);
      /*
       * The flag says whether this *deploy* has verified delivery. It does not say whether this
       * *order* was sold under it, and only the second question decides whether this panel applies.
       *
       * An order created before the flag went on has no management capability, no verified channel,
       * and a ticket that was emailed the ordinary way. Showing it the panel offered that customer a
       * recovery that cannot complete — recovery only sends to a verified channel — under copy
       * claiming their ticket had gone to "the channel you verified", which they never did.
       */
      if (publicState.deliveryContractVersion < 2) {
        setUnavailable(true);
        return;
      }
      if (token) setState(await deliveryApi.getState(orderCode, token, eventSlug));
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 401)) setUnavailable(true);
      else setError(err instanceof ApiError ? err.message : 'โหลดสถานะช่องทางรับตั๋วไม่สำเร็จ');
    } finally {
      setProbed(true);
    }
  }, [orderCode, eventSlug, token]);

  useEffect(() => { void load(); }, [load]);

  /**
   * TICK-169 — email magic links open a separate tab. Refresh when the buyer returns to this tab
   * while an email/SMS challenge is pending, so the order page stops claiming delivery is blocked
   * as soon as the server knows the channel was verified. This is event-driven instead of a
   * background poll, keeping the public status endpoint quiet while the tab is not in use.
   */
  useEffect(() => {
    const waitingForCodeChannel = state?.primary?.status === 'challenge_sent'
      && (state.primary.channel === 'email' || state.primary.channel === 'sms');
    if (!waitingForCodeChannel) return undefined;
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshWhenVisible);
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshWhenVisible);
    };
  }, [state?.primary?.status, state?.primary?.channel, load]);

  useEffect(() => {
    if (state?.ticketReady && !readyNotified.current) {
      readyNotified.current = true;
      onTicketReady?.();
    }
  }, [state?.ticketReady, onTicketReady]);

  // Bound identity, no friendship yet: the customer is off in the LINE app tapping "add friend",
  // and the follow webhook lands here without them doing anything else.
  const awaitingFriendship = !!state?.line?.bound && state.line.friendship !== 'friend'
    && state.primary?.channel === 'line';
  useEffect(() => {
    if (!awaitingFriendship) return;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (attempts > FRIENDSHIP_POLL_ATTEMPTS) {
        clearInterval(timer);
        return;
      }
      void load();
    }, FRIENDSHIP_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [awaitingFriendship, load]);

  const runAction = async (
    key: DeliveryChannel | 'refresh' | 'clear' | 'resend' | 'otp' | 'recovery-otp', action: () => Promise<void>,
  ) => {
    if (busy) return;
    setBusy(key);
    setError('');
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'ทำรายการไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setBusy(null);
    }
  };

  /**
   * R4 — asks the server to send proof to the channel already verified on this order or, when none
   * is verified yet, to the email/phone recorded at checkout (TICK-167). Only the channel type goes
   * up. The response never says whether the order or the contact exists, so the confirmation shown
   * here is worded to be true either way rather than implying something was found.
   */
  const requestRecovery = (channel: 'email' | 'sms') => runAction(channel, async () => {
    const result = await deliveryApi.requestRecovery(orderCode, channel, eventSlug);
    setRecoverySent(result.message);
    setRecoveryChannel(channel);
    setRecoveryOtp('');
  });

  /**
   * The SMS half: the code is the credential. The capability is kept only once the server accepts
   * it, and the panel then reloads as the buyer's own — channel state, and the ticket if it is ready.
   */
  const submitRecoveryOtp = () => runAction('recovery-otp', async () => {
    const result = await deliveryApi.verifyRecoveryOtp(orderCode, recoveryOtp.trim(), eventSlug);
    rememberManagementToken(orderCode, result.managementToken);
    setRecoveredToken(result.managementToken);
    setRecoverySent('');
    setRecoveryChannel(null);
    setRecoveryOtp('');
  });

  if (!probed || unavailable) return null;

  /*
   * TICK-090 — nothing to choose in the instant modes.
   *
   * The ticket is already on the page above, and the whole point of those modes is that the buyer
   * does not have to prove control of a channel first. Rendering the picker anyway would ask for
   * work that changes nothing and, worse, would read as though the ticket on screen were provisional
   * until they did it. The recovery notice below is skipped for the same reason: there is no
   * verified channel to recover *to*.
   */
  if (state?.instantTicket) return null;

  /*
   * R4 — no capability in this tab. Ordinary causes: the customer closed the browser, opened the
   * order link on their laptop, or is in a private window where the write silently failed.
   *
   * The way back is to prove control of a contact already on the order: the verified channel, or —
   * TICK-167, when nothing is verified yet — the email or phone given at checkout. Both buttons are
   * offered regardless of what we know about the order, the server's answer is the same either way,
   * and no contact is ever shown here — showing "recovery available" (or a masked address) only for
   * real orders would turn this into an order-code oracle.
   */
  if (!token) {
    return (
      <section className="delivery-panel" aria-labelledby="delivery-panel-title">
        <h2 id="delivery-panel-title">กู้คืนการจัดการคำสั่งซื้อ</h2>
        <p className="delivery-panel-lead">
          หน้านี้เปิดจากอุปกรณ์หรือเบราว์เซอร์อื่น จึงจัดการช่องทางรับตั๋วจากที่นี่ไม่ได้
          — ขอลิงก์หรือรหัสยืนยันไปยังอีเมลหรือเบอร์โทรที่ใช้ตอนสั่งซื้อ เพื่อยืนยันว่าเป็นคุณ
        </p>
        <div className="delivery-friendship-actions">
          <button className="btn-primary" disabled={busy !== null} onClick={() => requestRecovery('email')}>
            {busy === 'email' ? 'กำลังส่ง…' : 'ส่งลิงก์ยืนยันไปยังอีเมลที่ใช้ตอนสั่งซื้อ'}
          </button>
          <button className="btn-ghost" disabled={busy !== null} onClick={() => requestRecovery('sms')}>
            {busy === 'sms' ? 'กำลังส่ง…' : 'ส่งรหัสยืนยันไปยังเบอร์ที่ใช้ตอนสั่งซื้อ'}
          </button>
        </div>
        {recoverySent && (
          <div className="delivery-callout delivery-callout--ok" role="status">
            {recoverySent}
            <br />
            {recoveryChannel === 'sms'
              ? 'ถ้าได้รับ SMS ให้กรอกรหัสด้านล่าง'
              : 'ถ้าได้รับอีเมล ให้เปิดลิงก์เพื่อยืนยัน (ใช้ได้ครั้งเดียว หมดอายุใน 15 นาที) ถ้าไม่พบ ลองดูในกล่องจดหมายขยะ'}
          </div>
        )}
        {recoveryChannel === 'sms' && (
          <div className="delivery-challenge">
            <label htmlFor="delivery-recovery-otp">กรอกรหัสยืนยัน 6 หลัก</label>
            <input
              id="delivery-recovery-otp"
              className="delivery-otp-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={recoveryOtp}
              onChange={(e) => setRecoveryOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              aria-describedby="delivery-recovery-otp-hint"
            />
            <div className="delivery-friendship-actions">
              <button
                className="btn-primary"
                disabled={busy !== null || recoveryOtp.length !== 6}
                onClick={submitRecoveryOtp}
              >
                {busy === 'recovery-otp' ? 'กำลังตรวจสอบ…' : 'ยืนยันรหัสและไปต่อ'}
              </button>
            </div>
            <span id="delivery-recovery-otp-hint" className="delivery-option-hint">
              รหัสหมดอายุใน 5 นาที · ใส่ผิดได้ไม่เกิน 5 ครั้ง · ขอรหัสใหม่ได้ทุก 60 วินาที
            </span>
          </div>
        )}
        {error && <div className="delivery-callout delivery-callout--error" role="alert">{error}</div>}
        <p className="delivery-option-hint" data-testid="delivery-recovery-support">
          ระบบส่งไปยังอีเมลหรือเบอร์ที่บันทึกไว้กับคำสั่งซื้อนี้เท่านั้น เปลี่ยนปลายทางจากหน้านี้ไม่ได้
          หากเข้าถึงอีเมลหรือเบอร์เดิมไม่ได้แล้ว กรุณาติดต่อผู้จัดงาน{contactEmail ? ` ${contactEmail}` : ''} พร้อมรหัสคำสั่งซื้อ {orderCode}
          — ตั๋วและการชำระเงินของคุณไม่ได้รับผลกระทบ
        </p>
      </section>
    );
  }

  const chooseLine = () => runAction('line', async () => {
    const { authorizationUrl } = await deliveryApi.startLineBinding(orderCode, token, eventSlug);
    // A full navigation, not a popup: LINE's in-app browser blocks popups, and the OIDC round trip
    // has to come back to this same tab for sessionStorage to still hold the capability.
    window.location.assign(authorizationUrl);
  });

  const recheckFriendship = () => runAction('refresh', async () => {
    const { authorizationUrl } = await deliveryApi.refreshLineFriendship(orderCode, token, eventSlug);
    window.location.assign(authorizationUrl);
  });

  // The way out of a half-finished selection. Once a channel is verified the server refuses this,
  // because dropping a proven recipient is a recovery decision rather than a preference change.
  const clearSelection = () => runAction('clear', async () => {
    setState(await deliveryApi.clearPrimary(orderCode, token, eventSlug));
    setCallbackOutcome(null);
    setChallenge(null);
  });

  const chooseCodeChannel = (channel: 'email' | 'sms') => runAction(channel, async () => {
    setState(await deliveryApi.setPrimary(orderCode, channel, token, eventSlug));
    const issued = await deliveryApi.requestChallenge(orderCode, token, eventSlug);
    setChallenge(issued);
    setState(issued.state);
    setOtp('');
  });

  const resendChallenge = () => runAction('resend', async () => {
    const issued = await deliveryApi.requestChallenge(orderCode, token, eventSlug);
    setChallenge(issued);
    setState(issued.state);
    setOtp('');
  });

  const submitOtp = () => runAction('otp', async () => {
    if (!challenge) return;
    setState(await deliveryApi.verifyOtp(orderCode, challenge.challengeId, otp.trim(), token, eventSlug));
    setChallenge(null);
    setOtp('');
  });

  const callback = callbackOutcome ? CALLBACK_MESSAGES[callbackOutcome] : null;
  const lineOption = state?.options.find((option) => option.channel === 'line');
  const primary = state?.primary ?? null;
  const primaryVerified = primary?.status === 'verified';
  /*
   * QA (2026-08-27): the choices stay on screen while the slip is being checked, but are not
   * actionable yet — customers were tapping a channel the moment they landed, and the LINE option is
   * a full navigation out to an OAuth round trip, which is a bad thing to start while a payment is
   * still unresolved.
   *
   * Visible-but-disabled rather than hidden, deliberately. Hiding it removed the only thing that
   * tells a customer a channel is still owed, and `sendVerifiedTicketNotifications` sends nothing to
   * a v2 order without one — so the ticket went nowhere, silently. The server-side counterpart to
   * this gate is the reminder sent when payment lands with no channel chosen; neither half is safe
   * on its own.
   */
  const awaitingPayment = orderStatus !== 'paid';
  const actionsLocked = busy !== null || awaitingPayment;
  /*
   * TICK-159 — paid, nothing verified, and not one option this order can use. Three greyed-out buttons
   * and no words was the whole page on staging; say what is true and who can help.
   */
  const noChannelUsable = !!state && !awaitingPayment && !primaryVerified && state.options.every((o) => !o.available);

  return (
    <section className="delivery-panel" aria-labelledby="delivery-panel-title">
      <h2 id="delivery-panel-title">
        {primaryVerified ? 'ช่องทางรับตั๋ว' : 'เลือกช่องทางรับตั๋ว'}
      </h2>
      {!primaryVerified && (
        <p className="delivery-panel-lead">
          เลือกได้หนึ่งช่องทาง — เมื่อยืนยันการชำระเงินแล้ว ระบบจะส่งบัตรไปที่ช่องทางที่ยืนยันไว้เท่านั้น
        </p>
      )}

      {awaitingPayment && !primaryVerified && (
        <div className="delivery-callout delivery-callout--warn" role="status" data-testid="delivery-awaiting-payment">
          ยังเลือกไม่ได้ในตอนนี้ — รอระบบยืนยันการชำระเงินก่อน แล้วปุ่มด้านล่างจะกดได้
          <br />
          เราจะส่งข้อความแจ้งเตือนไปหาคุณเมื่อถึงขั้นตอนนี้ ไม่ต้องเฝ้าหน้านี้
        </div>
      )}

      {noChannelUsable && (
        <div className="delivery-callout delivery-callout--warn" role="status" data-testid="delivery-no-channel">
          ตอนนี้ยังไม่มีช่องทางรับตั๋วที่ใช้ได้สำหรับคำสั่งซื้อนี้
          <br />
          กรุณาติดต่อผู้จัดงาน{contactEmail ? ` ${contactEmail}` : ''} พร้อมรหัส {orderCode} — เมื่อเปิดช่องทางแล้ว เลือกรับตั๋วได้จากหน้านี้
        </div>
      )}

      {callback && (
        <div className={`delivery-callout delivery-callout--${callback.tone}`} role="status">{callback.text}</div>
      )}
      {error && <div className="delivery-callout delivery-callout--error" role="alert">{error}</div>}

      {!state ? (
        <div className="delivery-panel-loading" aria-live="polite">กำลังโหลดช่องทางรับตั๋ว…</div>
      ) : primaryVerified && primary ? (
        /*
         * QA (2026-08-27): once a channel is verified the server refuses any change to it, so
         * showing the full picker again just invited a tap that landed on "ต้องยืนยันตัวตนก่อน
         * เปลี่ยนช่องทางที่ยืนยันแล้ว". Show the settled state instead of the selection UI.
         */
        <div className="delivery-option delivery-option--selected" data-testid="delivery-channel-confirmed">
          <div className="delivery-option-head">
            <strong>{CHANNEL_LABELS[primary.channel]}</strong>
            <span className="delivery-badge delivery-badge--ok">ยืนยันแล้ว ✓</span>
          </div>
          <p className="delivery-option-hint">ระบบจะส่งบัตรไปที่ช่องทางนี้เท่านั้น</p>
          {primary.recipientMasked && primary.channel !== 'line' && (
            <p className="delivery-option-recipient">{primary.recipientMasked}</p>
          )}
        </div>
      ) : (
        <ul className="delivery-options">
          {state.options.map((option) => {
            const selected = primary?.channel === option.channel && primary.status !== 'revoked';
            const verified = selected && primary?.status === 'verified';
            return (
              <li
                key={option.channel}
                className={`delivery-option${selected ? ' delivery-option--selected' : ''}${option.available ? '' : ' delivery-option--disabled'}`}
              >
                <div className="delivery-option-head">
                  <strong>{CHANNEL_LABELS[option.channel]}</strong>
                  {option.channel === 'line' && <span className="delivery-badge">แนะนำ</span>}
                  {verified && <span className="delivery-badge delivery-badge--ok">ยืนยันแล้ว ✓</span>}
                </div>
                <p className="delivery-option-hint">
                  {option.available ? CHANNEL_HINTS[option.channel] : UNAVAILABLE_HINTS[option.unavailableReason ?? 'CHANNEL_DISABLED']}
                </p>
                {option.recipientMasked && option.channel !== 'line' && (
                  <p className="delivery-option-recipient">{option.recipientMasked}</p>
                )}
                {option.channel === 'line' && option.available && !verified && (
                  <button className="btn-primary" disabled={actionsLocked} onClick={chooseLine}>
                    {busy === 'line' ? 'กำลังเปิด LINE…' : state.line?.bound ? 'เชื่อมต่อ LINE อีกครั้ง' : 'เชื่อมต่อ LINE และเพิ่มเพื่อน JamTick'}
                  </button>
                )}
                {option.channel !== 'line' && option.available && !verified && (
                  <button
                    className="btn-primary"
                    disabled={actionsLocked}
                    onClick={() => chooseCodeChannel(option.channel as 'email' | 'sms')}
                  >
                    {busy === option.channel
                      ? 'กำลังส่ง…'
                      : option.channel === 'email' ? 'ส่งลิงก์ยืนยันไปที่อีเมลนี้' : 'ส่ง OTP ไปที่เบอร์นี้'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/*
        TICK-063 — the send failed, so nothing here may read as though a message is on its way.
        Rendered *instead of* the two screens below, not beside them: a customer who is told a code
        was sent and also told it was not will act on the first half and go looking for it.
      */}
      {challenge && !challenge.delivered && primary?.status !== 'verified' && (
        <div className="delivery-challenge delivery-challenge--undelivered" role="alert">
          <p data-testid="challenge-undelivered">
            {challenge.kind === 'email_magic_link'
              ? 'ยังส่งอีเมลยืนยันไม่สำเร็จ — ยังไม่มีลิงก์ถูกส่งออกไป'
              : 'ยังส่ง SMS รหัสยืนยันไม่สำเร็จ — ยังไม่มีรหัสถูกส่งออกไป'}
          </p>
          <span className="delivery-option-hint">
            {/*
              Two causes, one customer-facing distinction: whether waiting is worth anything.
              `provider_disabled` is our configuration and will come back; `provider_rejected` means
              the provider refused this particular message. Neither exposes which provider, what it
              said, or any status code — that belongs in the ledger support reads, not on a page the
              customer can screenshot.
            */}
            {challenge.deliveryBlockedReason === 'provider_disabled'
              ? 'ช่องทางนี้ส่งข้อความไม่ได้ชั่วคราว — ลองอีกครั้งในอีกสักครู่ หรือเลือกช่องทางอื่น'
              : 'ส่งไม่สำเร็จในขณะนี้ — ลองอีกครั้ง หรือเลือกช่องทางอื่น'}
          </span>
          <div className="delivery-friendship-actions">
            <button className="btn-primary" disabled={busy !== null} onClick={resendChallenge} data-testid="challenge-retry">
              {busy === 'resend' ? 'กำลังลองใหม่…' : 'ลองส่งอีกครั้ง'}
            </button>
          </div>
        </div>
      )}

      {/* Screen C-Email — nothing to type. The customer's job is in their inbox, so say only that. */}
      {challenge?.kind === 'email_magic_link' && challenge.delivered && primary?.status !== 'verified' && (
        <div className="delivery-challenge" role="status">
          <p data-testid="challenge-sent-email">ส่งลิงก์ยืนยันไปที่ {primary?.recipientMasked} แล้ว — เปิดลิงก์ในอีเมลเพื่อยืนยัน</p>
          <span className="delivery-option-hint">
            ลิงก์ใช้ได้ครั้งเดียวและหมดอายุใน 15 นาที ถ้าไม่พบ ลองดูในกล่องจดหมายขยะ
          </span>
          <button className="btn-ghost" disabled={busy !== null} onClick={resendChallenge}>
            {busy === 'resend' ? 'กำลังส่งใหม่…' : 'ส่งลิงก์อีกครั้ง'}
          </button>
        </div>
      )}

      {/* Screen C-SMS — six digits, and an explicit count of what is left before a fresh code. */}
      {challenge?.kind === 'sms_otp' && challenge.delivered && primary?.status !== 'verified' && (
        <div className="delivery-challenge">
          <label htmlFor="delivery-otp" data-testid="challenge-sent-sms">ใส่รหัส 6 หลักที่ส่งไปยัง {primary?.recipientMasked}</label>
          <input
            id="delivery-otp"
            className="delivery-otp-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            aria-describedby="delivery-otp-hint"
          />
          <div className="delivery-friendship-actions">
            <button className="btn-primary" disabled={busy !== null || otp.length !== 6} onClick={submitOtp}>
              {busy === 'otp' ? 'กำลังตรวจสอบ…' : 'ยืนยันรหัส'}
            </button>
            <button className="btn-ghost" disabled={busy !== null} onClick={resendChallenge}>
              {busy === 'resend' ? 'กำลังส่งใหม่…' : 'ขอรหัสใหม่'}
            </button>
          </div>
          <span id="delivery-otp-hint" className="delivery-option-hint">
            รหัสหมดอายุใน 5 นาที · ใส่ผิดได้ไม่เกิน 5 ครั้ง · ขอรหัสใหม่ได้ทุก 60 วินาที
          </span>
        </div>
      )}

      {/*
        R3 — bound but not a friend. The identity is proven and stays proven; what is missing is the
        one thing that lets the OA send a message at all, so this is a next step rather than a failure.
      */}
      {state?.line?.bound && state.line.friendship !== 'friend' && primary?.channel === 'line' && (
        <div className="delivery-friendship" role="status">
          <p>
            เชื่อมบัญชี LINE กับคำสั่งซื้อนี้แล้ว — เหลืออีกขั้นตอนเดียว:
            เพิ่ม JamTick เป็นเพื่อนใน LINE เพื่อให้ระบบส่งบัตรถึงคุณได้
          </p>
          <div className="delivery-friendship-actions">
            {state.line.addFriendUrl && (
              <a className="btn-primary" href={state.line.addFriendUrl} target="_blank" rel="noreferrer">
                เพิ่มเพื่อน JamTick
              </a>
            )}
            <button className="btn-ghost" disabled={busy !== null} onClick={recheckFriendship}>
              {busy === 'refresh' ? 'กำลังตรวจสอบ…' : 'ตรวจสอบอีกครั้ง'}
            </button>
          </div>
          <span className="delivery-option-hint">หน้านี้จะอัปเดตเองเมื่อเพิ่มเพื่อนสำเร็จ</span>
        </div>
      )}

      {/*
        A chosen-but-unverified channel is a dead end until it verifies: the ticket will not be sent
        to an unverified recipient, so the customer needs a visible way back rather than a page that
        just keeps waiting. Absent once verified — the server refuses it then, and offering a button
        that always fails would be worse than not offering one.
      */}
      {primary && primary.status !== 'verified' && (
        <div className="delivery-clear">
          <button className="btn-ghost" disabled={busy !== null} onClick={clearSelection}>
            {busy === 'clear' ? 'กำลังยกเลิก…' : `ยกเลิกการเลือก ${CHANNEL_LABELS[primary.channel]}`}
          </button>
          <span className="delivery-option-hint">
            ยกเลิกแล้วระบบจะส่งบัตรตามช่องทางเดิมที่กรอกไว้ตอนชำระเงิน และเลือกใหม่ได้ทุกเมื่อ
          </span>
        </div>
      )}

      {/*
        Screen D — the two states are independent, so the page has to say which half is outstanding.
        A customer who has verified their channel must never read "waiting" as "the payment failed".
      */}
      {state && !state.ticketReady && (
        <p className="delivery-panel-waiting">
          {primary?.status === 'verified' && orderStatus !== 'paid'
            ? 'ยืนยันช่องทางรับตั๋วแล้ว ✓ กำลังตรวจสอบการชำระเงิน — บัตรจะถูกส่งทันทีที่ตรวจสอบเสร็จ'
            : orderStatus === 'paid'
              ? 'ยืนยันการชำระเงินแล้ว ✓ เหลือเพียงยืนยันช่องทางรับตั๋วเพื่อรับ QR เข้างาน'
              : 'รอระบบยืนยันการชำระเงินก่อน แล้วจึงเลือกช่องทางรับตั๋วได้'}
        </p>
      )}
    </section>
  );
}
